import React, { useState, useCallback, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import { Input } from "antd"
import styles from "./index.module.css"

/**
 * Optimized OTP Input Component
 *
 * Features:
 * - Configurable number of digits
 * - Auto-focus management
 * - Paste support
 * - Keyboard navigation (arrow keys)
 * - Flexible validation (numeric/alphanumeric)
 * - Better performance with useCallback
 * - Properly integrates with Ant Design Form
 *
 * @example
 * <Form.Item name="otp" rules={[{ required: true }]}>
 *   <OTPInput
 *     length={6}
 *     onComplete={(otp) => console.log(otp)}
 *     type="numeric"
 *   />
 * </Form.Item>
 */
export const OTPInput = ({
  value = [],
  onChange,
  length = 6,
  type = "numeric", // 'numeric' | 'alphanumeric'
  autoFocus = true,
  disabled = false,
  placeholder = "0",
  className = "",
  inputClassName = "",
  onComplete,
  validatePattern
}) => {
  // Initialize OTP from value prop or empty array
  const [otp, setOtp] = useState(() => {
    const initialValue = Array.isArray(value) ? value : []
    return new Array(length)
      .fill("")
      .map((_, idx) => initialValue[idx] || "")
  })
  const inputRefs = useRef([])

  // Sync with external value changes
  useEffect(() => {
    if (Array.isArray(value) && value.length > 0) {
      const newOtp = new Array(length)
        .fill("")
        .map((_, idx) => value[idx] || "")
      setOtp(newOtp)
    }
  }, [value, length])

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length)
  }, [length])

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [autoFocus])

  // Get validation pattern based on type
  const getValidationPattern = useCallback(() => {
    if (validatePattern) return validatePattern
    return type === "numeric" ? /^\d$/ : /^[a-zA-Z0-9]$/
  }, [type, validatePattern])

  // Trigger onChange callback to Form.Item
  const triggerChange = useCallback(
    (newOtp) => {
      if (onChange) {
        onChange(newOtp)
      }

      // Call onComplete if all digits filled
      const isComplete = newOtp.every((digit) => digit !== "")
      if (isComplete && onComplete) {
        onComplete(newOtp.join(""))
      }
    },
    [onChange, onComplete]
  )

  // Handle input change
  const handleChange = useCallback(
    (value, index) => {
      const pattern = getValidationPattern()

      // Allow only valid characters
      if (value && !pattern.test(value)) {
        return
      }

      const newOtp = [...otp]
      newOtp[index] = value

      setOtp(newOtp)
      triggerChange(newOtp)

      // Auto-focus next input if value entered
      if (value && index < length - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    },
    [otp, length, triggerChange, getValidationPattern]
  )

  // Handle backspace/delete
  const handleKeyDown = useCallback(
    (e, index) => {
      // Backspace
      if (e.key === "Backspace") {
        e.preventDefault()
        const newOtp = [...otp]

        if (otp[index]) {
          // Clear current input
          newOtp[index] = ""
          setOtp(newOtp)
          triggerChange(newOtp)
        } else if (index > 0) {
          // Move to previous input and clear it
          newOtp[index - 1] = ""
          setOtp(newOtp)
          triggerChange(newOtp)
          inputRefs.current[index - 1]?.focus()
        }
      }
      // Arrow Left
      else if (e.key === "ArrowLeft" && index > 0) {
        e.preventDefault()
        inputRefs.current[index - 1]?.focus()
      }
      // Arrow Right
      else if (e.key === "ArrowRight" && index < length - 1) {
        e.preventDefault()
        inputRefs.current[index + 1]?.focus()
      }
    },
    [otp, length, triggerChange]
  )

  // Handle paste
  const handlePaste = useCallback(
    (e, index) => {
      e.preventDefault()
      const pastedData = e.clipboardData.getData("text").trim()
      const pattern = getValidationPattern()
      const newOtp = [...otp]

      // Extract valid characters from pasted data
      let pasteIndex = 0
      let i = index
      while (i < length && pasteIndex < pastedData.length) {
        const char = pastedData[pasteIndex]
        if (pattern.test(char)) {
          newOtp[i] = char
          i++
        }
        pasteIndex++
      }

      setOtp(newOtp)
      triggerChange(newOtp)

      // Focus the next empty input or last input
      const nextEmptyIndex = newOtp.findIndex((digit, idx) => idx >= index && digit === "")
      let focusIndex
      if (nextEmptyIndex === -1) {
        focusIndex = length - 1
      } else {
        focusIndex = nextEmptyIndex
      }
      inputRefs.current[focusIndex]?.focus()
    },
    [otp, length, triggerChange, getValidationPattern]
  )

  // Handle input focus
  const handleFocus = useCallback((e) => {
    e.target.select()
  }, [])

  return (
    <div className={`${styles.otpInputContainer} ${className}`}>
      {otp.map((digit, index) => (
        <Input
          key={`otp-input-${index}-${length}`}
          ref={(el) => (inputRefs.current[index] = el?.input || el)}
          value={digit}
          placeholder={placeholder}
          maxLength={1}
          disabled={disabled}
          className={`${styles.otpInput} ${inputClassName}`}
          onChange={(e) => handleChange(e.target.value, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={(e) => handlePaste(e, index)}
          onFocus={handleFocus}
          aria-label={`OTP digit ${index + 1} of ${length}`}
          inputMode={type === "numeric" ? "numeric" : "text"}
          autoComplete={index === 0 ? "one-time-code" : "off"}
        />
      ))}
    </div>
  )
}

OTPInput.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  length: PropTypes.number,
  type: PropTypes.oneOf(["numeric", "alphanumeric"]),
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  onComplete: PropTypes.func,
  validatePattern: PropTypes.instanceOf(RegExp)
}

export default OTPInput
